/**
 * Public Landing Page — trang chủ công khai.
 *
 * Style: giữ STRUCTURE của marketingarena.vn (top nav · hero · stats · overview ·
 * timeline · rules · awards · partners · contact · footer) NHƯNG dùng nhận diện
 * thương hiệu ICFGL UEL — sáng/trắng, navy + hot pink accent, sans-serif uppercase,
 * minimalist academic, không tech-dark.
 *
 * Nội dung lấy từ src/config/siteContent.ts.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MobileStepper,
  Pagination,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Gavel,
  Login,
  EmojiEvents,
  ArrowDownward,
  AccountBalance,
  Diversity3,
  Menu as MenuIcon,
  Close as CloseIcon,
  Article,
  Groups,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Schedule,
  Handshake,
  Mail,
  Visibility,
  CheckCircle,
  ExpandMore,
  Search as SearchIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { siteContent } from '../config/siteContent'
import { brand, gradientBrand, gradientBackdrop } from '../theme/brand'
import { useAppSelector } from '../hooks/redux'
import LanguageSwitcher from '../components/LanguageSwitcher'
import { useGetPublicTeamsQuery, type PublicTeam, type PublicTeamMember } from '../store/api/teamsApi'
import {
  useGetPublicResultsCasesQuery,
  useGetPublicLatestResultsQuery,
  useGetPublicScheduleQuery,
} from '../store/api/reportsApi'

// ─── Tokens — light academic palette (ICFGL UEL) ──────────────────────────
const BG_PRIMARY = '#FFFFFF'
const BG_SOFT = '#FAFBFD'
const BG_PINK_SOFT = brand.lightPink // #FFEBF2 — pink wash accent
const INK = brand.navy // #144E8C
const TEXT_BODY = '#2A3548'
const TEXT_DIM = '#5E6B7E'
const HAIRLINE = 'rgba(20,78,140,0.12)'

// NAV_LINKS được khởi tạo INSIDE TopBar component để dùng useTranslation hook.

// Eyebrow tag — navy uppercase letter-spaced (ICFGL academic feel)
function Eyebrow({ children, dark = false }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <Typography
      sx={{
        fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: '0.76rem',
        letterSpacing: 3,
        color: dark ? 'rgba(255,255,255,0.78)' : brand.hotPink,
        fontWeight: 700,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </Typography>
  )
}

function SectionHeader({
  eyebrow,
  title,
  subtitle,
  align = 'left',
}: {
  eyebrow: string
  title: string
  subtitle?: string
  align?: 'left' | 'center'
}) {
  return (
    <Stack
      spacing={2}
      alignItems={align === 'center' ? 'center' : 'flex-start'}
      textAlign={align === 'center' ? 'center' : 'left'}
      mb={{ xs: 4, md: 6 }}
    >
      <Eyebrow>{eyebrow}</Eyebrow>
      <Typography
        component="h2"
        sx={{
          color: INK,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: -0.5,
          fontSize: { xs: '1.75rem', md: '2.4rem' },
          lineHeight: 1.12,
        }}
      >
        {title}
      </Typography>
      {/* Horizontal divider rule — ICFGL marker */}
      <Box
        sx={{
          width: 64,
          height: 3,
          background: gradientBrand,
          borderRadius: 1,
          alignSelf: align === 'center' ? 'center' : 'flex-start',
        }}
      />
      {subtitle && (
        <Typography sx={{ color: TEXT_DIM, maxWidth: 720, fontSize: '1.02rem', lineHeight: 1.7 }}>
          {subtitle}
        </Typography>
      )}
    </Stack>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Top Nav — white/blur khi scroll, transparent khi top hero
// ═══════════════════════════════════════════════════════════════════════
function TopBar({ isAuthenticated, scrolled }: { isAuthenticated: boolean; scrolled: boolean }) {
  const theme = useTheme()
  // Breakpoint `lg` (1200px) — desktop có thể vừa 7 link + switcher + login button;
  // dưới ngưỡng này dùng hamburger menu để tránh wrap.
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  // Trimmed nav (7 link essential) — các section còn lại (Schedule, Results,
  // Organizers, Teams, Partners) vẫn render trên trang, user scroll xuống xem.
  const NAV_LINKS = [
    { label: t('nav.home'), href: '#hero' },
    { label: t('nav.about'), href: '#overview' },
    { label: t('nav.timeline'), href: '#timeline' },
    { label: t('nav.rules'), href: '#rules' },
    { label: t('nav.awards'), href: '#awards' },
    { label: t('nav.faq'), href: '#faq' },
    { label: t('nav.contact'), href: '#contact' },
  ]

  const textOnHero = !scrolled

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: scrolled ? 'rgba(255,255,255,0.96)' : 'transparent',
          backdropFilter: scrolled ? 'blur(14px)' : 'none',
          borderBottom: scrolled ? `1px solid ${HAIRLINE}` : '1px solid transparent',
          color: scrolled ? INK : 'common.white',
          transition: 'all 0.25s ease',
        }}
      >
        <Toolbar sx={{ maxWidth: 1280, width: '100%', mx: 'auto', minHeight: { xs: 60, md: 72 }, gap: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.2}
            sx={{ minWidth: 0, flexShrink: 1 }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="Phiên Toà Giả Định - Công Lý Giới"
              sx={{
                width: 40,
                height: 40,
                objectFit: 'contain',
                flexShrink: 0,
                filter: scrolled
                  ? 'drop-shadow(0 2px 6px rgba(20,78,140,0.2))'
                  : 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))',
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                  letterSpacing: 0.5,
                  lineHeight: 1.1,
                  color: textOnHero ? 'common.white' : INK,
                }}
              >
                {t('brand.short')}
              </Typography>
              <Typography
                noWrap
                sx={{
                  fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                  color: brand.hotPink,
                  fontSize: '0.66rem',
                  letterSpacing: 2,
                  lineHeight: 1,
                  fontWeight: 700,
                }}
              >
                {t('brand.season')}
              </Typography>
            </Box>
          </Stack>

          {!isMobile ? (
            <Stack
              direction="row"
              spacing={0.25}
              sx={{ flexGrow: 1, justifyContent: 'center' }}
            >
              {NAV_LINKS.map((link) => (
                <Button
                  key={link.href}
                  href={link.href}
                  sx={{
                    color: textOnHero ? 'rgba(255,255,255,0.85)' : TEXT_BODY,
                    fontWeight: 500,
                    fontSize: '0.82rem',
                    textTransform: 'none',
                    px: 1.1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: textOnHero ? 'common.white' : INK,
                      background: textOnHero ? 'rgba(255,255,255,0.08)' : 'rgba(20,78,140,0.06)',
                    },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Stack>
          ) : (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                onClick={() => setOpen((v) => !v)}
                sx={{ color: textOnHero ? 'common.white' : INK }}
              >
                {open ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            </Box>
          )}

          <Box sx={{ mr: 1, flexShrink: 0 }}>
            <LanguageSwitcher dark={textOnHero} />
          </Box>

          <Button
            component={RouterLink}
            to={isAuthenticated ? '/dashboard' : '/login'}
            variant="contained"
            size="small"
            startIcon={<Login />}
            sx={{
              background: brand.hotPink,
              color: 'common.white',
              fontWeight: 700,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              boxShadow: '0 6px 18px rgba(246,65,131,0.3)',
              '&:hover': { background: '#d9356f' },
            }}
          >
            {isAuthenticated ? t('topbar.dashboard') : t('topbar.login')}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Mobile drop-down */}
      {isMobile && open && (
        <Box
          sx={{
            position: 'fixed',
            top: 60,
            left: 0,
            right: 0,
            zIndex: 1200,
            background: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(14px)',
            borderBottom: `1px solid ${HAIRLINE}`,
            py: 2,
          }}
        >
          <Stack>
            {NAV_LINKS.map((link) => (
              <Button
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                sx={{
                  color: TEXT_BODY,
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  px: 3,
                  py: 1.2,
                  borderRadius: 0,
                  '&:hover': { color: INK, background: 'rgba(20,78,140,0.05)' },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Stack>
        </Box>
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Hero — gradientBackdrop (navy → pink → white) đúng ICFGL feel
// ═══════════════════════════════════════════════════════════════════════
function HeroSection({ isAuthenticated }: { isAuthenticated: boolean }) {
  const { t } = useTranslation()

  return (
    <Box
      id="hero"
      sx={{
        position: 'relative',
        background: gradientBackdrop, // navy → light blue → light pink → white
        color: 'common.white',
        pt: { xs: 14, md: 18 },
        pb: { xs: 12, md: 16 },
        overflow: 'hidden',
      }}
    >
      {/* Soft pink halo top-right — feminist accent */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          width: 540,
          height: 540,
          top: -160,
          right: -160,
          background: 'radial-gradient(circle, rgba(246,65,131,0.22) 0%, transparent 65%)',
          filter: 'blur(20px)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Stack
          spacing={{ xs: 3, md: 3.5 }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          textAlign={{ xs: 'left', md: 'center' }}
        >
          <Eyebrow dark>{t('hero.eyebrow')}</Eyebrow>

          <Typography
            component="h1"
            sx={{
              fontWeight: 900,
              lineHeight: 1.05,
              letterSpacing: -1,
              fontSize: { xs: '2.3rem', sm: '3.2rem', md: '4.2rem' },
              maxWidth: 980,
              color: 'common.white',
              textShadow: '0 4px 24px rgba(0,0,0,0.18)',
            }}
          >
            {t('hero.title')}
          </Typography>

          <Typography
            sx={{
              color: 'common.white',
              fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              letterSpacing: 4,
              fontWeight: 700,
              opacity: 0.92,
            }}
          >
            {t('hero.subtitle')}
          </Typography>

          <Box
            sx={{
              width: 80,
              height: 3,
              background: brand.hotPink,
              borderRadius: 1,
              alignSelf: { xs: 'flex-start', md: 'center' },
            }}
          />

          <Typography
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 720,
              fontSize: { xs: '1rem', md: '1.12rem' },
              lineHeight: 1.7,
            }}
          >
            {t('hero.tagline')}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 2 }}>
            <Button
              component={RouterLink}
              to={isAuthenticated ? '/dashboard' : '/login'}
              variant="contained"
              size="large"
              startIcon={<Login />}
              sx={{
                background: brand.hotPink,
                color: 'common.white',
                fontWeight: 800,
                px: 4.5,
                py: 1.5,
                fontSize: '1rem',
                letterSpacing: 1,
                boxShadow: '0 12px 32px rgba(246,65,131,0.4)',
                '&:hover': { background: '#d9356f' },
              }}
            >
              {isAuthenticated ? t('topbar.dashboard') : t('hero.ctaPrimary')}
            </Button>
            <Button
              href="#overview"
              variant="outlined"
              size="large"
              endIcon={<ArrowDownward />}
              sx={{
                color: 'common.white',
                borderColor: 'rgba(255,255,255,0.55)',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'common.white',
                  background: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              {t('hero.ctaSecondary')}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Stats — white bg với số gradient brand
// ═══════════════════════════════════════════════════════════════════════
function StatsSection() {
  const { t } = useTranslation()
  // Stats values là metric cứng — giữ nguyên từ siteContent. Label dịch qua i18n.
  const statLabelKeys = ['stats.teams', 'stats.universities', 'stats.judges', 'stats.rounds'] as const
  const stats = siteContent.stats.map((s, idx) => ({
    value: s.value,
    label: t(statLabelKeys[idx] ?? 'stats.teams'),
  }))

  return (
    <Box sx={{ background: BG_PRIMARY, borderBottom: `1px solid ${HAIRLINE}`, py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 2, md: 0 }}>
          {stats.map((s, idx) => (
            <Grid
              item
              xs={6}
              md={3}
              key={statLabelKeys[idx] ?? `stat-${idx}`}
              sx={{
                textAlign: 'center',
                borderRight: {
                  md: idx < stats.length - 1 ? `1px solid ${HAIRLINE}` : 'none',
                },
                py: { xs: 2, md: 3 },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2.2rem', md: '3rem' },
                  letterSpacing: -1,
                  background: gradientBrand,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: 1.05,
                }}
              >
                {s.value}
              </Typography>
              <Typography
                sx={{
                  color: TEXT_DIM,
                  fontSize: '0.78rem',
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  mt: 1,
                }}
              >
                {s.label}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Overview — white bg
// ═══════════════════════════════════════════════════════════════════════
function OverviewSection() {
  const { t } = useTranslation()
  const icons = [<Gavel />, <AccountBalance />, <Diversity3 />, <EmojiEvents />]
  const highlights = t('overview.highlights', { returnObjects: true }) as Array<{
    title: string
    description: string
  }>

  return (
    <Box id="overview" sx={{ py: { xs: 8, md: 12 }, background: BG_PRIMARY }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('overview.eyebrow')}
          title={t('overview.title')}
          subtitle={t('overview.description')}
        />

        <Grid container spacing={3}>
          {highlights.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={item.title}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  p: 3,
                  borderRadius: 2,
                  background: BG_PRIMARY,
                  border: `1px solid ${HAIRLINE}`,
                  transition: 'all 0.25s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: brand.hotPink,
                    boxShadow: '0 16px 32px rgba(20,78,140,0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1.5,
                    background: gradientBrand,
                    color: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                    boxShadow: '0 8px 18px rgba(20,78,140,0.18)',
                  }}
                >
                  {icons[index % icons.length]}
                </Box>
                <Typography sx={{ color: INK, fontWeight: 700, fontSize: '1.05rem', mb: 1 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ color: TEXT_DIM, fontSize: '0.92rem', lineHeight: 1.65 }}>
                  {item.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Timeline — off-white section, 3 phase cards
// ═══════════════════════════════════════════════════════════════════════
function TimelineSection() {
  const { t } = useTranslation()
  const phases = t('timeline.phases', { returnObjects: true }) as Array<{
    code: string
    title: string
    date: string
    description: string
  }>
  return (
    <Box id="timeline" sx={{ py: { xs: 8, md: 12 }, background: BG_SOFT }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('timeline.eyebrow')}
          title={t('timeline.title')}
          subtitle={t('timeline.subtitle')}
        />

        <Stack spacing={2.5}>
          {phases.map((p, idx) => (
            <Paper
              key={p.code}
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                background: BG_PRIMARY,
                border: `1px solid ${HAIRLINE}`,
                borderRadius: 2,
                display: 'flex',
                gap: { xs: 2, md: 4 },
                alignItems: 'flex-start',
                flexDirection: { xs: 'column', md: 'row' },
                transition: 'all 0.25s',
                '&:hover': {
                  borderColor: brand.hotPink,
                  boxShadow: '0 16px 32px rgba(20,78,140,0.1)',
                },
              }}
            >
              <Stack
                direction={{ xs: 'row', md: 'column' }}
                alignItems={{ xs: 'center', md: 'flex-start' }}
                spacing={{ xs: 2, md: 1 }}
                sx={{ minWidth: { md: 160 } }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                    fontWeight: 900,
                    fontSize: { xs: '1.5rem', md: '2.4rem' },
                    letterSpacing: -1,
                    background: gradientBrand,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {p.code}
                </Typography>
                <Typography
                  sx={{
                    color: TEXT_DIM,
                    fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                    fontSize: '0.74rem',
                    letterSpacing: 1.5,
                    fontWeight: 600,
                  }}
                >
                  PHASE · {String(idx + 1).padStart(2, '0')}
                </Typography>
              </Stack>

              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.2rem', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  {p.title}
                </Typography>
                <Typography
                  sx={{
                    color: brand.hotPink,
                    fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                    fontSize: '0.82rem',
                    letterSpacing: 1.5,
                    fontWeight: 700,
                    mb: 1.5,
                  }}
                >
                  {p.date}
                </Typography>
                <Typography sx={{ color: TEXT_BODY, fontSize: '0.95rem', lineHeight: 1.75 }}>
                  {p.description}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Schedule — lịch các phiên Bán kết / Chung kết (khi bracket confirmed)
// ═══════════════════════════════════════════════════════════════════════
const DURATION_LITERAL = '45–75 phút'

function ScheduleCaseRounds({ caseId, caseTitle }: { caseId: string; caseTitle: string }) {
  const { data, isLoading, isError } = useGetPublicScheduleQuery(caseId, {
    pollingInterval: 60_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: BG_PRIMARY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Paper>
    )
  }

  if (isError || !data || data.rounds.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          background: BG_SOFT,
          border: `1px dashed ${HAIRLINE}`,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ color: INK, fontWeight: 700, mb: 1 }}>
          {caseTitle}
        </Typography>
        <Typography sx={{ color: TEXT_DIM, fontSize: '0.92rem' }}>
          Lịch sẽ được công bố sau khi Ban Tổ chức bốc thăm cặp đấu cho Vòng Bán kết và Chung kết.
        </Typography>
      </Paper>
    )
  }

  return (
    <Stack spacing={2.5}>
      {data.rounds.map((rnd) => {
        const info = ROUND_LABEL(rnd.round_type)
        const dateStr = rnd.scheduled_date
          ? new Date(rnd.scheduled_date).toLocaleDateString('vi-VN', {
              weekday: 'long',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })
          : 'Sẽ công bố'

        return (
          <Paper
            key={rnd.round_id}
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3.5 },
              background: BG_PRIMARY,
              border: `1px solid ${HAIRLINE}`,
              borderRadius: 2,
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 2, md: 4 }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              sx={{ mb: 2 }}
            >
              <Typography
                sx={{
                  fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                  fontWeight: 900,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                  letterSpacing: -1,
                  background: gradientBrand,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  minWidth: 80,
                }}
              >
                {info.code}
              </Typography>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.05rem' }}>
                  {info.label.toUpperCase()} · {rnd.matches.length} cặp đấu
                </Typography>
                <Typography sx={{ color: TEXT_DIM, fontSize: '0.85rem', mt: 0.5 }}>
                  {dateStr}
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={1.5}>
              {rnd.matches.map((m, idx) => {
                const timeStr = m.started_at
                  ? new Date(m.started_at).toLocaleTimeString('vi-VN', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : 'Giờ sẽ công bố'
                return (
                  <Paper
                    key={`${rnd.round_id}_${idx}`}
                    elevation={0}
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      background: BG_SOFT,
                      borderLeft: `4px solid ${brand.hotPink}`,
                      borderRadius: 1.5,
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Schedule sx={{ fontSize: 18, color: TEXT_DIM }} />
                          <Box>
                            <Typography
                              sx={{
                                color: INK,
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                              }}
                            >
                              {timeStr}
                            </Typography>
                            <Typography sx={{ color: TEXT_DIM, fontSize: '0.75rem' }}>
                              {DURATION_LITERAL}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={9}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={{ xs: 1, sm: 2 }}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{ color: TEXT_DIM, fontSize: '0.7rem', letterSpacing: 1, fontWeight: 600 }}
                            >
                              NGUYÊN ĐƠN
                            </Typography>
                            <Typography sx={{ color: INK, fontWeight: 700, fontSize: '0.95rem' }}>
                              {m.team_plaintiff?.name || '—'}
                            </Typography>
                            {m.team_plaintiff?.university && (
                              <Typography sx={{ color: TEXT_DIM, fontSize: '0.78rem' }}>
                                {m.team_plaintiff.university}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              background: gradientBrand,
                              color: 'common.white',
                              fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                              fontWeight: 900,
                              fontSize: '0.75rem',
                              borderRadius: 1,
                              letterSpacing: 1,
                            }}
                          >
                            VS
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{ color: TEXT_DIM, fontSize: '0.7rem', letterSpacing: 1, fontWeight: 600 }}
                            >
                              BỊ ĐƠN
                            </Typography>
                            <Typography sx={{ color: INK, fontWeight: 700, fontSize: '0.95rem' }}>
                              {m.team_defendant?.name || '—'}
                            </Typography>
                            {m.team_defendant?.university && (
                              <Typography sx={{ color: TEXT_DIM, fontSize: '0.78rem' }}>
                                {m.team_defendant.university}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>
                )
              })}
            </Stack>
          </Paper>
        )
      })}
    </Stack>
  )
}

function ScheduleSection() {
  const { t } = useTranslation()
  const { data: cases = [], isLoading } = useGetPublicResultsCasesQuery(undefined, {
    pollingInterval: 60_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })

  return (
    <Box id="schedule" sx={{ py: { xs: 8, md: 12 }, background: BG_SOFT }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('schedule.eyebrow')}
          title={t('schedule.title')}
          subtitle={t('schedule.subtitle')}
        />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={32} />
          </Box>
        ) : cases.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              background: BG_PRIMARY,
              border: `1px dashed ${HAIRLINE}`,
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: INK, fontWeight: 700, mb: 1 }}>
              {t('schedule.empty')}
            </Typography>
            <Typography sx={{ color: TEXT_DIM, fontSize: '0.92rem' }}>
              {t('schedule.emptyBody')}
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={4}>
            {cases.map((c) => (
              <ScheduleCaseRounds key={c.case_id} caseId={c.case_id} caseTitle={c.title} />
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Results — kết quả các vòng thi đã công bố (Top N theo tên đội)
// ═══════════════════════════════════════════════════════════════════════
function ROUND_LABEL(
  round_type?: string | null,
): { code: string; label: string; advanceLabel: string } {
  switch (round_type) {
    case 'memorial':
      return { code: 'R1', label: 'Vòng Sơ loại', advanceLabel: 'đội vào Bán kết' }
    case 'semifinal':
      return { code: 'R2', label: 'Vòng Bán kết', advanceLabel: 'đội vào Chung kết' }
    case 'final':
      return { code: 'R3', label: 'Vòng Chung kết', advanceLabel: 'đội vô địch' }
    default:
      return { code: '—', label: round_type || 'Vòng thi', advanceLabel: 'đội đi tiếp' }
  }
}

function ResultsCaseCard({ caseId, title }: { caseId: string; title: string }) {
  const { data, isLoading } = useGetPublicLatestResultsQuery(caseId, {
    pollingInterval: 60_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: BG_PRIMARY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Paper>
    )
  }

  if (!data) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: BG_PRIMARY,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 2,
        }}
      >
        <Typography sx={{ color: INK, fontWeight: 700, mb: 0.5 }}>{title}</Typography>
        <Typography sx={{ color: TEXT_DIM, fontSize: '0.9rem' }}>
          Chưa có vòng nào công bố cho cuộc thi này.
        </Typography>
      </Paper>
    )
  }

  const roundInfo = ROUND_LABEL(data.round?.round_type)
  const publishedDate = data.published_at
    ? new Date(data.published_at).toLocaleDateString('vi-VN')
    : '—'

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3.5 },
        background: BG_PRIMARY,
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 2,
        transition: 'all 0.25s',
        '&:hover': {
          borderColor: brand.hotPink,
          boxShadow: '0 16px 32px rgba(20,78,140,0.08)',
        },
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 4 }} alignItems="flex-start">
        <Stack
          direction={{ xs: 'row', md: 'column' }}
          alignItems={{ xs: 'center', md: 'flex-start' }}
          spacing={{ xs: 2, md: 1 }}
          sx={{ minWidth: { md: 140 } }}
        >
          <Typography
            sx={{
              fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontWeight: 900,
              fontSize: { xs: '1.6rem', md: '2.4rem' },
              letterSpacing: -1,
              background: gradientBrand,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {roundInfo.code}
          </Typography>
          <Chip
            label="Đã công bố"
            size="small"
            color="success"
            sx={{ fontWeight: 700, letterSpacing: 0.3 }}
          />
        </Stack>

        <Box sx={{ flex: 1, width: '100%' }}>
          <Typography
            sx={{
              color: TEXT_DIM,
              fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontSize: '0.74rem',
              letterSpacing: 1.5,
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            {roundInfo.label.toUpperCase()} · CÔNG BỐ {publishedDate}
          </Typography>
          <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.15rem', mb: 2 }}>
            {data.case_title || title}
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Box>
              <Typography
                sx={{
                  fontSize: '1.6rem',
                  fontWeight: 800,
                  color: INK,
                  fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                  lineHeight: 1,
                }}
              >
                {data.total_teams}
              </Typography>
              <Typography sx={{ color: TEXT_DIM, fontSize: '0.78rem', mt: 0.5 }}>
                đội tham gia
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography
                sx={{
                  fontSize: '1.6rem',
                  fontWeight: 800,
                  color: brand.hotPink,
                  fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                  lineHeight: 1,
                }}
              >
                {data.advancing_count}
              </Typography>
              <Typography sx={{ color: TEXT_DIM, fontSize: '0.78rem', mt: 0.5 }}>
                {roundInfo.advanceLabel}
              </Typography>
            </Box>
          </Stack>

          <Typography sx={{ color: TEXT_DIM, fontSize: '0.78rem', mb: 1, fontStyle: 'italic' }}>
            (Sắp xếp theo tên đội — không theo thứ hạng, Điều 10 Thể lệ)
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {data.advancing_teams.map((team, idx) => (
              <Chip
                key={`${team.team_name}_${idx}`}
                label={team.team_name}
                size="small"
                sx={{
                  fontWeight: 700,
                  background: BG_SOFT,
                  border: `1px solid ${HAIRLINE}`,
                }}
              />
            ))}
          </Stack>

          <Button
            component={RouterLink}
            to={`/public/results/${caseId}`}
            variant="outlined"
            size="small"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Xem chi tiết →
          </Button>
        </Box>
      </Stack>
    </Paper>
  )
}

function ResultsSection() {
  const { t } = useTranslation()
  const { data: cases = [], isLoading, isError } = useGetPublicResultsCasesQuery(undefined, {
    pollingInterval: 60_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })

  return (
    <Box id="results" sx={{ py: { xs: 8, md: 12 }, background: BG_PRIMARY }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('resultsSection.eyebrow')}
          title={t('resultsSection.title')}
          subtitle={t('resultsSection.subtitle')}
        />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={32} />
          </Box>
        ) : isError || cases.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              background: BG_SOFT,
              border: `1px dashed ${HAIRLINE}`,
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: INK, fontWeight: 700, mb: 1 }}>
              {t('resultsSection.empty')}
            </Typography>
            <Typography sx={{ color: TEXT_DIM, fontSize: '0.92rem' }}>
              {t('resultsSection.emptyBody')}
            </Typography>
          </Paper>
        ) : (
          <>
            <Stack spacing={2.5}>
              {cases.map((c) => (
                <ResultsCaseCard key={c.case_id} caseId={c.case_id} title={c.title} />
              ))}
            </Stack>
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                component={RouterLink}
                to="/public/results"
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  background: gradientBrand,
                }}
              >
                {t('resultsSection.viewAll')}
              </Button>
            </Box>
          </>
        )}
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Rules — white bg, 2-column cards
// ═══════════════════════════════════════════════════════════════════════
function RulesSection() {
  const { t } = useTranslation()
  const articles = t('rules.articles', { returnObjects: true }) as Array<{
    title: string
    body: string
  }>

  return (
    <Box id="rules" sx={{ py: { xs: 8, md: 12 }, background: BG_PRIMARY }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('rules.eyebrow')}
          title={t('rules.title')}
          subtitle={t('rules.subtitle')}
        />

        <Grid container spacing={2.5}>
          {articles.map((article, index) => (
            <Grid item xs={12} md={6} key={article.title}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 3.5 },
                  height: '100%',
                  background: BG_PRIMARY,
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: 2,
                  display: 'flex',
                  gap: 2.5,
                  alignItems: 'flex-start',
                  transition: 'all 0.25s',
                  '&:hover': {
                    borderColor: brand.hotPink,
                    boxShadow: '0 12px 24px rgba(20,78,140,0.08)',
                  },
                }}
              >
                <Box
                  sx={{
                    minWidth: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: gradientBrand,
                    color: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '1.05rem',
                    fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                    boxShadow: '0 6px 14px rgba(20,78,140,0.18)',
                  }}
                >
                  {String(index + 1).padStart(2, '0')}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: INK, fontWeight: 700, fontSize: '1.05rem', mb: 1 }}>
                    {article.title}
                  </Typography>
                  <Typography sx={{ color: TEXT_BODY, fontSize: '0.92rem', lineHeight: 1.7 }}>
                    {article.body}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Awards — light pink section (feminist accent)
// ═══════════════════════════════════════════════════════════════════════
function AwardsSection() {
  const { t } = useTranslation()
  const awardItems = t('awards.items', { returnObjects: true }) as Array<{
    rank: string
    amount: string
    description?: string
  }>
  const medalColors = ['#C9A24E', '#9AA5B1', '#B07B3D', brand.hotPink]

  return (
    <Box id="awards" sx={{ py: { xs: 8, md: 12 }, background: BG_PINK_SOFT }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('awards.eyebrow')}
          title={t('awards.title')}
          subtitle={t('awards.subtitle')}
        />

        <Grid container spacing={3}>
          {awardItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={item.rank}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  background: BG_PRIMARY,
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  textAlign: 'center',
                  transition: 'all 0.25s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: medalColors[index % medalColors.length],
                    boxShadow: `0 16px 32px ${medalColors[index % medalColors.length]}25`,
                  },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: -40,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 180,
                    height: 180,
                    background: `radial-gradient(circle, ${medalColors[index % medalColors.length]}20 0%, transparent 70%)`,
                    pointerEvents: 'none',
                  }}
                />
                <Box sx={{ position: 'relative' }}>
                  <EmojiEvents sx={{ fontSize: 54, color: medalColors[index % medalColors.length], mb: 1.5 }} />
                  <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.18rem', mb: 0.5 }}>
                    {item.rank}
                  </Typography>
                  <Typography
                    sx={{
                      color: brand.hotPink,
                      fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                      fontSize: '0.8rem',
                      letterSpacing: 1,
                      fontWeight: 700,
                      mb: 1.5,
                    }}
                  >
                    {item.amount}
                  </Typography>
                  {item.description && (
                    <Typography sx={{ color: TEXT_BODY, fontSize: '0.88rem', lineHeight: 1.65 }}>
                      {item.description}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Public teams — approved team profiles for Guest users
// ═══════════════════════════════════════════════════════════════════════
function getInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (words.length === 0) return 'TV'
  return words.slice(-2).map((word) => word[0]).join('').toUpperCase()
}

function getDisplayMemberProfiles(team: PublicTeam): PublicTeamMember[] {
  if (team.member_profiles?.length) return team.member_profiles
  return Array.from({ length: team.member_count }, (_, index) => ({
    position: index + 1,
    role_label: index === 0 ? 'Đội trưởng' : `Thành viên ${index + 1}`,
    full_name: `Thành viên ${index + 1}`,
    university: team.university,
    faculty: null,
    bio: null,
    photo_url: null,
  }))
}

const TEAMS_PER_PAGE = 8

function PublicTeamsSection() {
  const { t } = useTranslation()
  const { data: teams = [], isLoading, isError } = useGetPublicTeamsQuery()
  const [selectedTeam, setSelectedTeam] = useState<PublicTeam | null>(null)
  const [activeSlide, setActiveSlide] = useState(0)
  const [page, setPage] = useState(1)
  const sectionRef = useRef<HTMLDivElement | null>(null)

  const totalPages = Math.max(1, Math.ceil(teams.length / TEAMS_PER_PAGE))

  useEffect(() => {
    if (page > totalPages) setPage(1)
  }, [totalPages, page])

  const pagedTeams = useMemo(() => {
    const start = (page - 1) * TEAMS_PER_PAGE
    return teams.slice(start, start + TEAMS_PER_PAGE)
  }, [teams, page])

  const handlePageChange = (_: unknown, value: number) => {
    setPage(value)
    if (sectionRef.current) {
      const top = sectionRef.current.getBoundingClientRect().top + window.scrollY - 80
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }

  const openTeamProfile = (team: PublicTeam) => {
    setSelectedTeam(team)
    setActiveSlide(0)
  }

  const closeTeamProfile = () => {
    setSelectedTeam(null)
    setActiveSlide(0)
  }

  const selectedMemberProfiles = selectedTeam ? getDisplayMemberProfiles(selectedTeam) : []
  const slidesCount = selectedTeam ? Math.max(1, 1 + selectedMemberProfiles.length) : 0

  const rangeStart = teams.length === 0 ? 0 : (page - 1) * TEAMS_PER_PAGE + 1
  const rangeEnd = Math.min(page * TEAMS_PER_PAGE, teams.length)

  return (
    <Box ref={sectionRef} id="teams" sx={{ py: { xs: 8, md: 12 }, background: BG_SOFT }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('teamsSection.eyebrow')}
          title={t('teamsSection.title')}
          subtitle={t('teamsSection.subtitle')}
        />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={32} />
          </Box>
        ) : isError ? (
          <Paper
            elevation={0}
            sx={{ p: 3, background: BG_PRIMARY, border: `1px solid ${HAIRLINE}`, borderRadius: 2 }}
          >
            <Typography sx={{ color: TEXT_DIM }}>
              Chưa tải được danh sách đội thi công khai.
            </Typography>
          </Paper>
        ) : teams.length === 0 ? (
          <Paper
            elevation={0}
            sx={{ p: 3, background: BG_PRIMARY, border: `1px solid ${HAIRLINE}`, borderRadius: 2 }}
          >
            <Typography sx={{ color: TEXT_DIM }}>
              Chưa có đội thi đã duyệt để hiển thị công khai.
            </Typography>
          </Paper>
        ) : (
          <>
          <Grid container spacing={3}>
            {pagedTeams.map((team) => (
              <Grid item xs={12} md={6} key={team.team_id}>
                <Paper
                  elevation={0}
                  onClick={() => openTeamProfile(team)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      openTeamProfile(team)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    background: BG_PRIMARY,
                    border: `1px solid ${HAIRLINE}`,
                    borderRadius: 2,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.25s',
                    '&:focus-visible': {
                      outline: `3px solid ${brand.hotPink}55`,
                      outlineOffset: 3,
                    },
                    '&:hover': {
                      borderColor: brand.hotPink,
                      boxShadow: '0 12px 26px rgba(20,78,140,0.08)',
                    },
                  }}
                >
                  <Stack spacing={2.25} sx={{ height: '100%' }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          background: gradientBrand,
                          color: 'common.white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Groups />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: INK,
                            fontWeight: 800,
                            fontSize: '1.08rem',
                            lineHeight: 1.3,
                            textTransform: 'uppercase',
                            wordBreak: 'break-word',
                          }}
                        >
                          {team.name}
                        </Typography>
                        <Typography sx={{ color: TEXT_DIM, fontSize: '0.9rem' }}>
                          {team.university}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={`${team.member_count} thành viên`} size="small" color="primary" variant="outlined" />
                      
                      <Chip label="Team Profile" size="small" color="secondary" variant="outlined" />
                      {team.final_rank ? (
                        <Chip label={`Hạng ${team.final_rank}`} size="small" color="warning" variant="outlined" />
                      ) : null}
                    </Stack>

                    <Typography sx={{ color: TEXT_BODY, lineHeight: 1.75, flexGrow: 1 }}>
                      {team.profile_text?.trim()
                        ? team.profile_text
                        : 'Đội đã được duyệt hồ sơ; Team Profile file được công khai khi có bản upload.'}
                    </Typography>

                    <Button
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={(event) => {
                        event.stopPropagation()
                        openTeamProfile(team)
                      }}
                      sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}
                    >
                      Xem Team Profile
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {teams.length > TEAMS_PER_PAGE && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 4 }}
            >
              <Typography sx={{ color: TEXT_DIM, fontSize: '0.85rem' }}>
                Hiển thị {rangeStart}–{rangeEnd} của {teams.length} đội
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                siblingCount={1}
                boundaryCount={1}
              />
            </Stack>
          )}
          </>
        )}
      </Container>

      <Dialog
        open={Boolean(selectedTeam)}
        onClose={closeTeamProfile}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            background: BG_PRIMARY,
          },
        }}
      >
        {selectedTeam && (
          <>
            <DialogTitle
              sx={{
                p: 0,
                background: gradientBackdrop,
                color: 'common.white',
              }}
            >
              <Stack spacing={1.5} sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        background: 'rgba(255,255,255,0.18)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Groups />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.45rem' }, fontWeight: 900, lineHeight: 1.15 }}>
                        {selectedTeam.name}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                        {selectedTeam.university}
                      </Typography>
                    </Box>
                  </Stack>
                  <IconButton onClick={closeTeamProfile} sx={{ color: 'common.white' }}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`${selectedTeam.member_count} thành viên`} size="small" sx={{ color: INK, background: 'rgba(255,255,255,0.92)' }} />
                  <Chip label="Đã duyệt" size="small" sx={{ color: '#0B6B3A', background: 'rgba(236,253,245,0.94)' }} />
                  {selectedTeam.final_rank ? (
                    <Chip label={`Hạng ${selectedTeam.final_rank}`} size="small" sx={{ color: '#7A4B00', background: 'rgba(255,247,214,0.96)' }} />
                  ) : null}
                </Stack>
              </Stack>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ minHeight: { xs: 430, md: 470 }, p: { xs: 2.5, md: 4 } }}>
                {activeSlide === 0 ? (
                  <Grid container spacing={3} alignItems="stretch">
                    <Grid item xs={12} md={7}>
                      <Stack spacing={2.5} sx={{ height: '100%' }}>
                        <Eyebrow>Team Profile</Eyebrow>
                        <Typography
                          component="h3"
                          sx={{
                            color: INK,
                            fontSize: { xs: '1.5rem', md: '2rem' },
                            fontWeight: 900,
                            lineHeight: 1.12,
                            textTransform: 'uppercase',
                          }}
                        >
                          {selectedTeam.name}
                        </Typography>
                        <Typography sx={{ color: TEXT_BODY, lineHeight: 1.85, fontSize: '1rem' }}>
                          {selectedTeam.profile_text?.trim()
                            ? selectedTeam.profile_text
                            : 'Đội đã được BTC duyệt hồ sơ và sẵn sàng tham gia các vòng thi chính thức.'}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip label={selectedTeam.university} color="primary" variant="outlined" />
                          <Chip label={`${selectedMemberProfiles.length || selectedTeam.member_count} thành viên`} color="secondary" variant="outlined" />
                          {selectedTeam.has_profile_file ? <Chip label="Có file Team Profile" color="success" /> : null}
                        </Stack>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Box
                        sx={{
                          height: '100%',
                          minHeight: 260,
                          borderRadius: 2,
                          background: BG_SOFT,
                          border: `1px solid ${HAIRLINE}`,
                          p: 2,
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: 1.5,
                        }}
                      >
                        {selectedMemberProfiles.map((profile, index) => {
                          const name = profile.full_name || `Thành viên ${index + 1}`
                          return (
                            <Stack key={`${name}-${index}`} spacing={1} alignItems="center" justifyContent="center">
                              <Avatar
                                src={profile.photo_url || undefined}
                                sx={{
                                  width: { xs: 66, md: 82 },
                                  height: { xs: 66, md: 82 },
                                  background: gradientBrand,
                                  color: 'common.white',
                                  fontWeight: 900,
                                  fontSize: '1.15rem',
                                }}
                              >
                                {getInitials(name)}
                              </Avatar>
                              <Typography sx={{ color: INK, fontWeight: 800, fontSize: '0.82rem', textAlign: 'center', lineHeight: 1.3 }}>
                                {name}
                              </Typography>
                            </Stack>
                          )
                        })}
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  (() => {
                    const member = selectedMemberProfiles[activeSlide - 1]
                    return (
                      <Grid container spacing={3.5} alignItems="center">
                        <Grid item xs={12} md={5}>
                          <Box
                            sx={{
                              borderRadius: 2,
                              background: BG_PINK_SOFT,
                              border: `1px solid ${HAIRLINE}`,
                              minHeight: { xs: 250, md: 360 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 3,
                            }}
                          >
                            <Avatar
                              src={member?.photo_url || undefined}
                              sx={{
                                width: { xs: 168, md: 220 },
                                height: { xs: 168, md: 220 },
                                background: gradientBrand,
                                color: 'common.white',
                                fontWeight: 900,
                                fontSize: { xs: '2.6rem', md: '3.25rem' },
                                boxShadow: '0 18px 40px rgba(20,78,140,0.18)',
                              }}
                            >
                              {getInitials(member?.full_name || `Thành viên ${activeSlide}`)}
                            </Avatar>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={7}>
                          <Stack spacing={2.25}>
                            <Eyebrow>{member?.role_label || `Thành viên ${activeSlide}`}</Eyebrow>
                            <Typography
                              component="h3"
                              sx={{
                                color: INK,
                                fontSize: { xs: '1.65rem', md: '2.35rem' },
                                fontWeight: 900,
                                lineHeight: 1.08,
                                textTransform: 'uppercase',
                              }}
                            >
                              {member?.full_name || `Thành viên ${activeSlide}`}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {member?.faculty ? <Chip label={member.faculty} color="primary" variant="outlined" /> : null}
                              <Chip label={member?.university || selectedTeam.university} color="secondary" variant="outlined" />
                            </Stack>
                            <Typography sx={{ color: TEXT_BODY, lineHeight: 1.85, fontSize: '1rem' }}>
                              {member?.bio || 'Thành viên đại diện đội tham gia tranh tụng và chuẩn bị hồ sơ thi đấu.'}
                            </Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    )
                  })()
                )}
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                px: { xs: 1.5, md: 2.5 },
                py: 1.5,
                borderTop: `1px solid ${HAIRLINE}`,
                justifyContent: 'stretch',
              }}
            >
              <MobileStepper
                variant="dots"
                steps={slidesCount}
                position="static"
                activeStep={activeSlide}
                sx={{ flexGrow: 1, background: 'transparent', px: 0 }}
                nextButton={
                  <Button
                    size="small"
                    onClick={() => setActiveSlide((step) => Math.min(slidesCount - 1, step + 1))}
                    disabled={activeSlide >= slidesCount - 1}
                    endIcon={<KeyboardArrowRight />}
                  >
                    Tiếp
                  </Button>
                }
                backButton={
                  <Button
                    size="small"
                    onClick={() => setActiveSlide((step) => Math.max(0, step - 1))}
                    disabled={activeSlide === 0}
                    startIcon={<KeyboardArrowLeft />}
                  >
                    Trước
                  </Button>
                }
              />
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Partners — white bg, logo placeholder tiles
// ═══════════════════════════════════════════════════════════════════════
function PartnersSection() {
  const { t } = useTranslation()
  // Logos data structure không dịch — giữ nguyên từ siteContent.
  const { partners } = siteContent

  return (
    <Box id="partners" sx={{ py: { xs: 8, md: 12 }, background: BG_PRIMARY }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('partners.eyebrow')}
          title={t('partners.title')}
          subtitle={t('partners.subtitle')}
          align="center"
        />
        <Grid container spacing={2}>
          {partners.logos.map((p) => (
            <Grid item xs={6} sm={4} md={3} key={p.name}>
              <Paper
                elevation={0}
                sx={{
                  height: 96,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: BG_SOFT,
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: 2,
                  px: 2,
                  textAlign: 'center',
                  transition: 'all 0.25s',
                  '&:hover': {
                    borderColor: brand.hotPink,
                    background: BG_PRIMARY,
                    boxShadow: '0 8px 18px rgba(20,78,140,0.08)',
                  },
                }}
              >
                <Typography sx={{ color: INK, fontSize: '0.88rem', fontWeight: 700 }}>
                  {p.name}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Contact — off-white section
// ═══════════════════════════════════════════════════════════════════════
function ContactSection() {
  const { t } = useTranslation()
  // Inline contact info — label dịch qua t(), value giữ giá trị thực tế (không dịch).
  const contactInfo: Array<{ label: string; value: string; icon: React.ReactElement }> = [
    { label: t('contact.email'), value: 'mootcourt@uel.edu.vn', icon: <Mail /> },
    { label: t('contact.hotline'), value: 'Sẽ được công bố', icon: <Schedule /> },
    { label: t('contact.fanpage'), value: 'facebook.com/uel.mootcourt', icon: <Handshake /> },
    { label: t('contact.website'), value: 'mootcourt.n2nai.io', icon: <Article /> },
  ]

  return (
    <Box id="contact" sx={{ py: { xs: 8, md: 12 }, background: BG_SOFT }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('contact.eyebrow')}
          title={t('contact.title')}
          subtitle={t('contact.subtitle')}
        />

        <Grid container spacing={3}>
          {contactInfo.map((info) => (
            <Grid item xs={12} sm={6} md={3} key={info.label}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  background: BG_PRIMARY,
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: 2,
                  transition: 'all 0.25s',
                  '&:hover': { borderColor: brand.hotPink, boxShadow: '0 12px 24px rgba(20,78,140,0.08)' },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 1.5,
                      background: gradientBrand,
                      color: 'common.white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 6px 14px rgba(20,78,140,0.18)',
                    }}
                  >
                    {info.icon}
                  </Box>
                  <Typography
                    sx={{
                      color: INK,
                      fontWeight: 800,
                      fontSize: '1.05rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    {info.label}
                  </Typography>
                </Stack>
                <Typography sx={{ color: TEXT_BODY, fontSize: '0.95rem', wordBreak: 'break-word' }}>
                  {info.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: { xs: 3, md: 4 },
            background: BG_PRIMARY,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ color: INK, fontWeight: 800, fontSize: '1.05rem', mb: 1 }}>
            {t('contact.stillHaveQuestions')}
          </Typography>
          <Typography sx={{ color: TEXT_BODY, fontSize: '0.95rem' }}>
            {t('contact.stillHaveQuestionsBody')}
          </Typography>
        </Paper>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Footer — navy bg anchor (reverse colors)
// ═══════════════════════════════════════════════════════════════════════
function FooterSection() {
  const { t } = useTranslation()

  return (
    <Box sx={{ py: 5, background: INK, color: 'common.white' }}>
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 1.2,
                background: 'rgba(255,255,255,0.96)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.4,
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="Phiên Toà Giả Định - Công Lý Giới"
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </Box>
            <Box>
              <Typography sx={{ color: 'common.white', fontWeight: 800, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {t('brand.short')}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>
                {t('footer.organizer')}
              </Typography>
            </Box>
          </Stack>
          <Divider
            sx={{ width: { xs: 60, md: 'auto' }, height: { md: 28 }, borderColor: 'rgba(255,255,255,0.2)' }}
            orientation={{ xs: 'horizontal', md: 'vertical' } as any}
          />
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.6)',
              fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
              fontSize: '0.78rem',
              letterSpacing: 1,
            }}
          >
            © {t('footer.copyright')}
          </Typography>
        </Stack>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Organizing Section — Ban Tổ chức & Hội đồng giám khảo
// ═══════════════════════════════════════════════════════════════════════
function OrganizingSection() {
  const { t } = useTranslation()
  const roles = t('organizing.committee.roles', { returnObjects: true }) as string[]
  const rounds = t('organizing.judges.rounds', { returnObjects: true }) as Array<{
    name: string
    body: string
  }>

  return (
    <Box
      id="organizers"
      sx={{
        py: { xs: 7, sm: 10 },
        background: `linear-gradient(180deg, ${BG_PRIMARY} 0%, rgba(255,235,242,0.3) 100%)`,
      }}
    >
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('organizing.eyebrow')}
          title={t('organizing.title')}
          subtitle={t('organizing.subtitle')}
        />

        <Grid container spacing={3}>
          {/* Card BTC */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                height: '100%',
                background: `linear-gradient(135deg, ${BG_PRIMARY} 0%, rgba(255,235,242,0.4) 100%)`,
                border: `1px solid ${HAIRLINE}`,
                borderRadius: 2,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start" mb={2}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: gradientBrand,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 6px 18px rgba(20,78,140,0.2)',
                  }}
                >
                  <Diversity3 sx={{ color: 'common.white', fontSize: 30 }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{ color: INK, fontWeight: 700, mb: 0.5, fontSize: '1.3rem' }}
                  >
                    {t('organizing.committee.title')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: TEXT_DIM, lineHeight: 1.6 }}>
                    {t('organizing.committee.host')}
                  </Typography>
                </Box>
              </Stack>

              <Box mt={3} mb={1.5}>
                <Eyebrow>{t('organizing.committee.rolesTitle')}</Eyebrow>
              </Box>
              <Stack spacing={1}>
                {roles.map((role, i) => (
                  <Stack key={i} direction="row" spacing={1.2} alignItems="flex-start">
                    <CheckCircle
                      sx={{ color: brand.hotPink, fontSize: 18, mt: 0.3, flexShrink: 0 }}
                    />
                    <Typography variant="body2" sx={{ color: TEXT_BODY, lineHeight: 1.65 }}>
                      {role}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>

          {/* Card Judges */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                height: '100%',
                background: `linear-gradient(135deg, ${BG_PRIMARY} 0%, rgba(20,78,140,0.04) 100%)`,
                border: `1px solid ${HAIRLINE}`,
                borderRadius: 2,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start" mb={2}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: gradientBrand,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 6px 18px rgba(20,78,140,0.2)',
                  }}
                >
                  <Gavel sx={{ color: 'common.white', fontSize: 28 }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{ color: INK, fontWeight: 700, mb: 0.5, fontSize: '1.3rem' }}
                  >
                    {t('organizing.judges.title')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: TEXT_DIM, lineHeight: 1.6 }}>
                    {t('organizing.judges.intro')}
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={1.5} mt={2}>
                {rounds.map((round, i) => (
                  <Box
                    key={i}
                    sx={{
                      borderLeft: `4px solid ${brand.hotPink}`,
                      p: 1.5,
                      background: BG_SOFT,
                      borderRadius: 1.5,
                    }}
                  >
                    <Chip
                      label={round.name}
                      size="small"
                      sx={{
                        background: INK,
                        color: 'common.white',
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        mb: 1,
                        height: 22,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: TEXT_BODY, lineHeight: 1.65 }}>
                      {round.body}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Pledge */}
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: 3,
            textAlign: 'center',
            background: `linear-gradient(135deg, rgba(246,65,131,0.08) 0%, rgba(20,78,140,0.08) 100%)`,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 2,
          }}
        >
          <Typography
            sx={{
              color: INK,
              fontStyle: 'italic',
              fontSize: { xs: '1rem', md: '1.1rem' },
              fontWeight: 500,
              lineHeight: 1.6,
            }}
          >
            {t('organizing.pledge')}
          </Typography>
        </Paper>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Media Section — thư viện khoảnh khắc
// ═══════════════════════════════════════════════════════════════════════
function MediaSection() {
  const { t } = useTranslation()
  const categories = t('media.categories', { returnObjects: true }) as Array<{
    title: string
    description: string
  }>
  const testimonials = t('media.testimonials', { returnObjects: true }) as Array<{
    quote: string
    author: string
  }>

  const categoryIcons = [Visibility, Diversity3, Groups, EmojiEvents, Article, Gavel]

  return (
    <Box id="media" sx={{ py: { xs: 6, sm: 9 }, background: BG_PRIMARY }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('media.eyebrow')}
          title={t('media.title')}
          subtitle={t('media.subtitle')}
          align="center"
        />
        <Typography
          sx={{
            color: TEXT_DIM,
            fontStyle: 'italic',
            textAlign: 'center',
            mt: -2,
            mb: 4,
            fontSize: '0.92rem',
          }}
        >
          {t('media.comingSoon')}
        </Typography>

        <Grid container spacing={3}>
          {categories.map((category, i) => {
            const IconCmp = categoryIcons[i % categoryIcons.length]
            return (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    border: `1px solid ${HAIRLINE}`,
                    borderRadius: 2,
                    background: BG_PRIMARY,
                    transition: 'all 0.25s',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      borderColor: INK,
                      boxShadow: '0 16px 32px rgba(20,78,140,0.12)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '16/9',
                      background: gradientBrand,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <IconCmp sx={{ color: 'common.white', fontSize: 48, opacity: 0.95 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ color: INK, fontWeight: 700, fontSize: '1.05rem', mb: 1 }}
                  >
                    {category.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: TEXT_DIM, lineHeight: 1.65 }}>
                    {category.description}
                  </Typography>
                </Paper>
              </Grid>
            )
          })}
        </Grid>

        {/* Testimonials */}
        <Stack
          direction="row"
          spacing={3}
          mt={5}
          sx={{ flexWrap: 'wrap', justifyContent: 'center', rowGap: 3 }}
        >
          {testimonials.map((testimonial, i) => (
            <Paper
              key={i}
              elevation={0}
              sx={{
                p: 2.5,
                maxWidth: 480,
                flexGrow: 1,
                background: BG_SOFT,
                borderLeft: `4px solid ${brand.hotPink}`,
                border: `1px solid ${HAIRLINE}`,
                borderLeftWidth: 4,
                borderRadius: 1.5,
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: INK, fontStyle: 'italic', lineHeight: 1.65 }}
              >
                &ldquo;{testimonial.quote}&rdquo;
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: TEXT_DIM,
                  display: 'block',
                  mt: 1.5,
                  fontWeight: 600,
                  letterSpacing: 0.3,
                }}
              >
                — {testimonial.author}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// FAQ Section — accordion with search
// ═══════════════════════════════════════════════════════════════════════
function FAQSection() {
  const { t } = useTranslation()
  const items = t('faq.items', { returnObjects: true }) as Array<{
    question: string
    answer: string
  }>
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter(
      (item) =>
        item.question.toLowerCase().includes(q) || item.answer.toLowerCase().includes(q),
    )
  }, [query, items])

  return (
    <Box id="faq" sx={{ py: { xs: 6, sm: 9 }, background: BG_SOFT }}>
      <Container maxWidth="lg">
        <SectionHeader
          eyebrow={t('faq.eyebrow')}
          title={t('faq.title')}
          subtitle={t('faq.subtitle')}
          align="center"
        />

        <TextField
          variant="filled"
          size="small"
          placeholder={t('faq.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{
            display: 'block',
            maxWidth: 520,
            mx: 'auto',
            mb: 4,
            background: BG_PRIMARY,
            borderRadius: 1.5,
            '& .MuiFilledInput-root': {
              background: BG_PRIMARY,
              borderRadius: 1.5,
              border: `1px solid ${HAIRLINE}`,
              '&:hover': { background: BG_PRIMARY },
              '&.Mui-focused': { background: BG_PRIMARY, borderColor: brand.hotPink },
              '&::before, &::after': { display: 'none' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: TEXT_DIM, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ maxWidth: 880, mx: 'auto' }}>
          {filtered.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('faq.emptyState')}
            </Alert>
          ) : (
            filtered.map((item, i) => (
              <Accordion
                key={i}
                disableGutters
                square={false}
                elevation={0}
                sx={{
                  mb: 1.5,
                  border: `1px solid ${HAIRLINE}`,
                  borderRadius: 2,
                  background: BG_PRIMARY,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    borderColor: brand.hotPink,
                    boxShadow: '0 8px 18px rgba(20,78,140,0.06)',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: INK }} />}
                  sx={{ px: 2.5, py: 0.5 }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: INK, fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 }}
                  >
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: TEXT_DIM, lineHeight: 1.7 }}
                  >
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Box>
      </Container>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// Main
// ═══════════════════════════════════════════════════════════════════════
export default function PublicLandingPage() {
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <Box sx={{ minHeight: '100dvh', background: BG_PRIMARY }}>
      <TopBar isAuthenticated={isAuthenticated} scrolled={scrolled} />
      <HeroSection isAuthenticated={isAuthenticated} />
      <StatsSection />
      <OverviewSection />
      <TimelineSection />
      <ScheduleSection />
      <ResultsSection />
      <RulesSection />
      <AwardsSection />
      <OrganizingSection />
      <PublicTeamsSection />
      <MediaSection />
      <PartnersSection />
      <FAQSection />
      <ContactSection />
      <FooterSection />
    </Box>
  )
}
